import { faThumbtack, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import { useState } from 'react';
import styled from 'styled-components';
import { CoachType, useSelfQuery } from '../../../graphql/types';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import useTracking from '../../../hooks/useTracking';
import {
  isLocalDateTodayOrInFuture,
  isLocalDateTodayOrInPast,
} from '../../../utils/date';
import { getEnvLevel } from '../../../utils/getEnvLevel';
import {
  EventNames,
  EventProfileCardNames,
} from '../../../utils/trackingEvents';
import Modal from '../../Modal';
import { AlertBanner } from '../../reusable/AlertBanner';
import { ButtonSize } from '../../reusable/Button';
import { IconButton } from '../../reusable/Button/IconButton';
import Text, { TextKind } from '../../reusable/Text';
import { SurveyConfig } from './config';

const IFrame = styled('iframe')`
  border: none;
  width: 768px;
  height: 520px;
`;

const DialogTitle = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const SurveyModal = ({
  isOpen,
  onClose,
  config,
  userId,
  coachType,
}: {
  isOpen: boolean;
  onClose: () => void;
  config: SurveyConfig | undefined;
  userId?: string;
  coachType?: CoachType | null | undefined;
}) => {
  const { track } = useTracking();
  const envLevel = getEnvLevel();

  const handleClose = (
    eventName: EventNames.CloseClick | EventNames.TapOut,
  ) => {
    track(eventName, {
      survey_id: config?.link,
      card_name: EventProfileCardNames.QualtricsSurveyModal,
    });
    onClose();
  };

  const url = config?.link ? new URL(config.link) : undefined;

  if (!url) {
    return null;
  }

  if (userId) {
    url.searchParams.set('user_id', userId);
  }
  if (coachType) {
    url.searchParams.set('coach_type', coachType);
  }
  if (envLevel) {
    url.searchParams.set('env_level', envLevel);
  }

  return (
    <Modal
      withPadding={false}
      isOpen={isOpen}
      onClose={() => handleClose(EventNames.TapOut)}
      width="770px"
    >
      <Modal.Header>
        <DialogTitle>
          <Text kind={TextKind.TitleS} component="h2">
            {config?.title}
          </Text>
          <IconButton
            size={ButtonSize.Small}
            icon={<FontAwesomeIcon icon={faXmark} fontSize={24} />}
            ariaLabel="close"
            onClick={() => handleClose(EventNames.CloseClick)}
          />
        </DialogTitle>
      </Modal.Header>
      <IFrame title={config?.title} src={url.toString()} />
    </Modal>
  );
};

export const CoachSurveyBanners = ({
  configs,
}: {
  configs: SurveyConfig[];
}) => {
  const [selectedSurveyConfig, setSelectedSurveyConfig] =
    useState<SurveyConfig>();
  const { data } = useSelfQuery({
    fetchPolicy: 'cache-first',
  });
  const { track } = useTracking();
  const { flags } = useFeatureFlags();

  // if there is a flag in the config, use it, otherwise displaying is based just on dates
  const flagResult = (config: SurveyConfig) =>
    config.splitFlag ? flags[config.splitFlag] : true;
  const shouldShow = (config: SurveyConfig) =>
    flagResult(config) &&
    isLocalDateTodayOrInFuture(config.dueDate) &&
    isLocalDateTodayOrInPast(config.startDate);

  const enabledConfigs = configs.filter(shouldShow);

  return (
    <>
      {enabledConfigs.map((config) => (
        <AlertBanner
          key={config.title}
          icon={faThumbtack}
          primaryText={`Pinned: ${config.title}`}
          secondaryText={`Due ${LocalDate.parse(config.dueDate).format(
            DateTimeFormatter.ofPattern('EE, MMM d').withLocale(Locale.US),
          )}`}
          ctaText="Open Survey"
          ctaOnClick={() => {
            track(EventNames.OpenSurveyClick, {
              survey_id: config.link,
              card_name: EventProfileCardNames.QualtricsSurveyModal,
            });
            setSelectedSurveyConfig(config);
          }}
        />
      ))}

      <SurveyModal
        isOpen={Boolean(selectedSurveyConfig)}
        onClose={() => setSelectedSurveyConfig(undefined)}
        config={selectedSurveyConfig}
        userId={data?.self?.id}
        coachType={data?.self?.coachData?.type}
      />
    </>
  );
};
